import { c as _c } from "react/compiler-runtime";
/**
 * Chain info hooks — now backed by ChainRegistryProvider.
 *
 * The registry loads once per session; these hooks are synchronous
 * lookups into that registry. No per-chain network calls.
 *
 * Public API is stable with the pre-registry implementation so call
 * sites don't need import-path or shape changes.
 */

import type { QueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useChainRegistry, chainRegistryQueryKey } from "@/components/providers/ChainRegistryProvider";
import { getChainIconUrl } from "@/app/api/chains/chainIcons";
import { CHAIN_INFO } from "@/lib/chains";
import { HL_CHAIN_ID } from "@/lib/hyperliquid/constants";

// Re-exported for consumers; also imported below for use within this file.
export type { ChainInfo } from "@/app/api/chains/shared";
import type { ChainInfo } from "@/app/api/chains/shared";

// Namespace root for TanStack Query invalidation. `invalidate(chainInfoKeys.all)`
// flushes the registry because `chainRegistryQueryKey` is `["chainInfo", "registry"]`.
export const chainInfoKeys = {
  all: ["chainInfo"] as const,
  registry: chainRegistryQueryKey
};

// Hyperliquid uses chain ID 1337 (EIP-712 signing domain) which collides with
// Geth Testnet. Override so the UI shows "Hyperliquid" everywhere.
const HL_CHAIN_INFO: ChainInfo = {
  chainId: HL_CHAIN_ID,
  name: "Hyperliquid",
  iconUrl: null
};

/** Resolve chain info: hardcoded overrides → registry → static fallback. */
function resolveChain(registryLookup: (id: number) => ChainInfo | null, chainId: number): ChainInfo | null {
  if (chainId === HL_CHAIN_ID) return HL_CHAIN_INFO;
  const fromRegistry = registryLookup(chainId);
  if (fromRegistry) return fromRegistry;
  const entry = CHAIN_INFO[chainId];
  if (!entry) return null;
  return {
    chainId,
    name: entry.name,
    iconUrl: getChainIconUrl(chainId)
  };
}

/**
 * Returns chain info for a single chainId. Reads from the registry —
 * no network call. While the registry is loading, returns `data: null`
 * with `isLoading: true`.
 */
export function useChainInfo(chainId) {
  const $ = _c(8);
  const registry = useChainRegistry();
  let t0;
  if ($[0] !== chainId || $[1] !== registry.getChain) {
    t0 = chainId ? resolveChain(registry.getChain, chainId) : null;
    $[0] = chainId;
    $[1] = registry.getChain;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const data = t0;
  const t1 = !!chainId && registry.isLoading && !data;
  const t2 = Boolean(registry.error);
  let t3;
  if ($[3] !== data || $[4] !== registry.error || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      data,
      isLoading: t1,
      isError: t2,
      error: registry.error
    };
    $[3] = data;
    $[4] = registry.error;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}

/**
 * Returns a Map of chainId → ChainInfo for the provided ids. Reads
 * from the registry; no network calls.
 */
export function useChainInfoMap(chainIds: number[]) {
  const registry = useChainRegistry();
  // Callers often pass a fresh array per render. Dedupe+sort once, then use
  // the joined string as the memo key below — strings compare by value so the
  // downstream map is stable across renders with the same id set.
  const sortedIds = useMemo(() => [...new Set(chainIds.filter(Boolean))].sort((a, b) => a - b), [chainIds]);
  const idsKey = sortedIds.join(",");
  const chainInfoMap = useMemo(() => {
    const map = new Map<number, ChainInfo>();
    for (const id of sortedIds) {
      const info = resolveChain(registry.getChain, id);
      if (info) map.set(id, info);
    }
    return map;
    // sortedIds intentionally omitted — idsKey is the stable value equivalent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, registry]);
  return {
    chainInfoMap,
    data: chainInfoMap,
    // No ids requested → not loading, even while the registry itself fetches.
    isLoading: !!idsKey && registry.isLoading && chainInfoMap.size === 0,
    isError: Boolean(registry.error),
    error: registry.error
  };
}

/**
 * Synchronous cache read — reads the registry directly via queryClient.
 * Returns undefined if the registry hasn't loaded yet.
 */
export function getCachedChainInfo(queryClient: QueryClient, chainId: number): ChainInfo | undefined {
  const registry = queryClient.getQueryData<Record<number, ChainInfo>>(chainRegistryQueryKey);
  return resolveChain(id => registry?.[id] ?? null, chainId) ?? undefined;
}